import {
  WEDDING_COLOR_SETS,
  WEDDING_SIZE_PROFILES,
  WEDDING_CATEGORY_ATTRIBUTE_MAP,
  getWeddingPreLovedAttributes,
} from './weddingPreLovedAttributes';

/** Wedding Essentials hub — category chips and API filter values */
export const BRIDAL_GOWN_API_CATEGORY = 'Bridal Gowns and Essentials';

/** In-studio gown fitting: 500 EGP on top of dress price; locations vary by listing. */
export const BRIDAL_GOWN_TRY_ON = {
  serviceFeeEgp: 500,
  fittingLocations: [
    'Zamalek bridal studio — Cairo',
    'New Cairo fitting suite (Tagamo\'a)',
    'Maadi appointment-only showroom',
  ],
  title: 'Try the gown before you buy',
  message:
    'Book an in-person fitting for 500 EGP on top of the dress price. Fittings are available at select locations — confirm with the seller when you order.',
};

export const SUITS_PROM_API_CATEGORY = 'Suits & Prom Wear';

export const EVERYDAY_CLOTHING_SECTION = 'Everyday Clothing';
export const GROOM_CLOTHING_SECTION = 'Groom';
export const BRIDAL_CLOTHING_SECTION = 'Bridal';
export const SOIREE_CLOTHING_SECTION = 'Soiree';
export const SUITS_PROM_CLOTHING_SECTION = 'Suits & Prom Wear';

export const CLOTHING_OCCASION_SECTIONS = [
  { section: GROOM_CLOTHING_SECTION, apiCategory: 'Groom Suits & Essentials' },
  { section: BRIDAL_CLOTHING_SECTION, apiCategory: BRIDAL_GOWN_API_CATEGORY },
  { section: SOIREE_CLOTHING_SECTION, apiCategory: 'Bridesmaids / soiree dresses' },
  { section: SUITS_PROM_CLOTHING_SECTION, apiCategory: SUITS_PROM_API_CATEGORY },
];

export const CLOTHING_OCCASION_SECTION_NAMES = CLOTHING_OCCASION_SECTIONS.map(
  (row) => row.section,
);

export const isOccasionClothingSection = (section) => (
  CLOTHING_OCCASION_SECTION_NAMES.includes(String(section || '').trim())
);

export const apiCategoryForClothingSection = (section) => {
  const match = CLOTHING_OCCASION_SECTIONS.find((row) => row.section === section);
  return match ? match.apiCategory : null;
};

export const clothingSectionForCategory = (category) => {
  const match = CLOTHING_OCCASION_SECTIONS.find((row) => row.apiCategory === category);
  return match ? match.section : EVERYDAY_CLOTHING_SECTION;
};

export const WEDDING_CATEGORIES = [
  { id: 'bridal-gowns', name: 'Bridal Gowns and Essentials', icon: '👰', apiCategory: BRIDAL_GOWN_API_CATEGORY },
  { id: 'bridesmaids', name: 'Bridesmaids / soiree dresses', icon: '👗', apiCategory: 'Bridesmaids / soiree dresses' },
  { id: 'bridal-veil', name: 'Bridal Veil', icon: '🕊️', apiCategory: 'Bridal Veil' },
  { id: 'bridal-accessories', name: 'Bridal Accessories', icon: '👑', apiCategory: 'Bridal Accessories' },
  { id: 'bridal-shoes', name: 'Bridal Shoes', icon: '👠', apiCategory: 'Bridal Shoes' },
  { id: 'groom-suits', name: 'Groom Suits & Essentials', icon: '🤵', apiCategory: 'Groom Suits & Essentials' },
  { id: 'grooms-accessories', name: 'Grooms Accessories', icon: '🎩', apiCategory: 'Grooms Accessories' },
  { id: 'men-shoes', name: 'Men Formal shoes', icon: '👞', apiCategory: 'Men Formal shoes' },
  { id: 'suits-prom', name: 'Suits & Prom Wear', icon: '🪩', apiCategory: SUITS_PROM_API_CATEGORY },
  { id: 'event-decor', name: 'Event Decorations', icon: '✨', apiCategory: 'Event Decorations' },
];

export const WEDDING_CATEGORY_VALUES = WEDDING_CATEGORIES.map((c) => c.apiCategory);
const WEDDING_CATEGORY_VALUE_SET = new Set(WEDDING_CATEGORY_VALUES);

export const isWeddingListingCategory = (category) => (
  WEDDING_CATEGORY_VALUE_SET.has(String(category || '').trim())
);

/** Wedding & Occasion Wear clothing categories eligible for partner fitting appointments. */
export const WEDDING_FITTING_APPOINTMENT_CATEGORIES = new Set([
  'Bridal Gowns and Essentials',
  'Bridesmaids / soiree dresses',
  'Bridesmaids Dresses',
  'Groom Suits & Essentials',
  'Bridal Shoes',
  'Men Formal shoes',
  'Men Shoes',
]);

export const supportsWeddingFittingAppointment = (category) => (
  WEDDING_FITTING_APPOINTMENT_CATEGORIES.has(String(category || '').trim())
);

/**
 * Legacy DB category names that still appear on older listings.
 * Browse must query these aliases or those rows never show on the wedding hub.
 */
export const WEDDING_CATEGORY_ALIASES = {
  'Bridesmaids / soiree dresses': ['Bridesmaids Dresses'],
  'Men Formal shoes': ['Men Shoes'],
};

/** Canonical category plus any legacy aliases to query. */
export const expandWeddingCategoryQueryValues = (category) => {
  const canonical = String(category || '').trim();
  if (!canonical) return [];
  const aliases = WEDDING_CATEGORY_ALIASES[canonical] || [];
  return [canonical, ...aliases.filter((alias) => alias && alias !== canonical)];
};

/**
 * Pre-owned / luxury Footwear paths that should also appear on the Wedding hub
 * (especially under Bridal Shoes). Dedicated wedding categories stay off
 * Pre-owned Curated Finds; everyday Footwear (heels, flats) can still appear
 * in both hubs.
 */
export const WEDDING_FOOTWEAR_CROSS_LIST = [
  { category: 'Footwear', subcategory: 'Women > Heels', weddingType: 'Heels' },
  { category: 'Footwear', subcategory: 'Women > Sandals', weddingType: 'Sandals' },
  { category: 'Footwear', subcategory: 'Women > Bridal Shoes', weddingType: null },
  { category: 'Footwear', subcategory: 'Women > Flats', weddingType: 'Flats' },
];

/**
 * Marketplace Footwear queries to merge into wedding browse.
 * Shown for All / Women, and when no category chip or Bridal Shoes is selected.
 */
export const getWeddingFootwearCrossListQueries = ({
  department = '',
  category = '',
  subcategory = '',
} = {}) => {
  if (String(department || '').trim() === 'Men') return [];
  const selectedCategory = String(category || '').trim();
  if (selectedCategory && selectedCategory !== 'Bridal Shoes') return [];

  const selectedType = String(subcategory || '').trim();
  const rows = selectedType
    ? WEDDING_FOOTWEAR_CROSS_LIST.filter((row) => {
        if (row.weddingType === selectedType) return true;
        // "Other" / unmatched wedding types still include dedicated Bridal Shoes leaf.
        return selectedType === 'Other' && row.weddingType == null;
      })
    : WEDDING_FOOTWEAR_CROSS_LIST;

  return rows.map(({ category: cat, subcategory: sub }) => ({
    category: cat,
    subcategory: sub,
  }));
};

/** Pre-loved wedding listings — categories match Wedding Essentials hub only */
export const WEDDING_PRE_LOVED_CATEGORY_DATA = {
  'Bridal Gowns and Essentials': [
    'Wedding Dresses',
    'Engagement Dresses',
    'Soiree Dresses',
  ],
  'Bridesmaids / soiree dresses': ['Bridesmaid Dresses', 'Evening Dresses', 'Other'],
  'Bridesmaids Dresses': ['Bridesmaid Dresses', 'Evening Dresses', 'Other'],
  'Bridal Veil': ['Cathedral Veil', 'Chapel Veil', 'Fingertip Veil', 'Birdcage Veil', 'Mantilla', 'Other'],
  'Bridal Accessories': ['Headpieces', 'Jewelry', 'Belts & Sashes', 'Gloves', 'Other'],
  'Bridal Shoes': ['Heels', 'Flats', 'Sandals', 'Other'],
  'Groom Suits & Essentials': [
    'Suits',
    'Tuxedos',
    'Blazers',
    'Dress Shirts',
    'Ties & Bow Ties',
    'Other',
  ],
  'Suits & Prom Wear': [
    'Suits',
    'Tuxedos',
    'Prom Dresses',
    'Blazers',
    'Other',
  ],
  'Grooms Accessories': ['Cufflinks', 'Tie Clips', 'Pocket Squares', 'Boutonnieres', 'Other'],
  'Men Formal shoes': ['Oxford', 'Loafers', 'Boots', 'Formal', 'Other'],
  'Men Shoes': ['Oxford', 'Loafers', 'Boots', 'Formal', 'Other'],
  'Event Decorations': [
    'Centerpieces',
    'Backdrops',
    'Table Settings',
    'Floral Arrangements',
    'Lighting',
    'Other',
  ],
};

/** Wedding pre-loved categories mapped to listing department filters. */
export const WEDDING_CATEGORY_DEPARTMENT_MAP = {
  'Bridal Gowns and Essentials': 'Women',
  'Bridesmaids Dresses': 'Women',
  'Bridesmaids / soiree dresses': 'Women',
  'Bridal Veil': 'Women',
  'Bridal Accessories': 'Women',
  'Bridal Shoes': 'Women',
  'Groom Suits & Essentials': 'Men',
  'Suits & Prom Wear': null,
  'Grooms Accessories': 'Men',
  'Men Shoes': 'Men',
  'Men Formal shoes': 'Men',
  'Event Decorations': null,
};

export const getWeddingCategoryDepartment = (category) => (
  Object.prototype.hasOwnProperty.call(WEDDING_CATEGORY_DEPARTMENT_MAP, category)
    ? WEDDING_CATEGORY_DEPARTMENT_MAP[category]
    : null
);

export const WEDDING_DEPARTMENTS = ['Men', 'Women'];

export const getWeddingCategoriesForDepartment = (department = '') => {
  if (!department) return WEDDING_CATEGORIES;
  return WEDDING_CATEGORIES.filter(
    (cat) => getWeddingCategoryDepartment(cat.apiCategory) === department,
  );
};

export const getWeddingCategoryValuesForDepartment = (department = '') => (
  getWeddingCategoriesForDepartment(department).flatMap((cat) =>
    expandWeddingCategoryQueryValues(cat.apiCategory),
  )
);

export {
  WEDDING_COLOR_SETS,
  WEDDING_SIZE_PROFILES,
  WEDDING_CATEGORY_ATTRIBUTE_MAP,
  getWeddingPreLovedAttributes,
};

/** Default bridal palette (legacy import) */
export const WEDDING_PRE_LOVED_COLORS = WEDDING_COLOR_SETS.bridal;

/** @deprecated Use getWeddingPreLovedAttributes(category) */
export const WEDDING_PRE_LOVED_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size'];
